import React, { useMemo } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Shift, DayTimeDeduction } from "@/types/index";
import { formatShiftTime, getDayName } from "@/utils/shiftFormUtils";
import { DayNumber } from "@/types";

interface ShiftSelectorProps {
  shifts: Shift[];
  selectedShiftId?: string; // This can be either shift.id or shift.code depending on context
  onShiftChange: (shiftId: string) => void;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  showShiftInfo?: boolean;
  otDate?: string;
  className?: string;
  compact?: boolean;
  uniqueId?: string; // Add unique identifier to prevent key conflicts
  useShiftCodeAsValue?: boolean; // New prop to control whether to use shift.code or shift.id as value
}

/**
 * Unified shift selection component that handles both simple and categorized shift selection
 * Replaces duplicate shift selection logic across multiple components
 */
export function ShiftSelector({
  shifts,
  selectedShiftId,
  onShiftChange,
  label = "Select Shift",
  placeholder = "Select a shift",
  disabled = false,
  showShiftInfo = false,
  className = "",
  compact = false,
  uniqueId = "default",
  useShiftCodeAsValue = false
}: ShiftSelectorProps) {
  
  const selectedShift = useMemo(() => {
    if (useShiftCodeAsValue) {
      return shifts.find(s => s.code === selectedShiftId);
    }
    // When not using shift codes, find by name (for edit mode) or id (for add mode)
    return shifts.find(s => s.name === selectedShiftId || s.id === selectedShiftId);
  }, [shifts, selectedShiftId, useShiftCodeAsValue]);

  return (
    <div className={`space-y-2 ${className}`}>
      <div className="space-y-1.5">
        <Label className={`${compact ? 'text-xs' : 'text-sm'} font-medium`}>
          {label}
        </Label>
        
        <Select 
          value={selectedShiftId || ""} 
          onValueChange={onShiftChange}
          disabled={disabled || shifts.length === 0}
        >
          <SelectTrigger className={compact ? "h-8 text-sm" : "h-9"}>
            <SelectValue placeholder={disabled ? "Loading..." : placeholder} />
          </SelectTrigger>
          <SelectContent>
            {/* Group shifts by name for better UX */}
            {(() => {
              // Group shifts by name
              const groupedShifts = shifts.reduce((acc, shift) => {
                if (!acc[shift.name]) {
                  acc[shift.name] = [];
                }
                acc[shift.name].push(shift);
                return acc;
              }, {} as Record<string, typeof shifts>);

              return Object.entries(groupedShifts).map(([shiftName, shiftGroup], groupIndex) => (
                <React.Fragment key={`group-${shiftName}-${groupIndex}`}>
                  {/* Show shift name as header if there are multiple shifts with same name */}
                  {shiftGroup.length > 1 && (
                    <div className="px-2 py-1 text-xs font-medium text-muted-foreground bg-muted/50">
                      {shiftName}
                    </div>
                  )}
                  {/* Show individual shifts */}
                  {shiftGroup.map((shift, index) => (
                    <SelectItem 
                      key={`shift-selector-${uniqueId}-${shift.id}-${shift.name}-${index}`} 
                      value={useShiftCodeAsValue ? (shift.code || shift.name) : shift.id}
                    >
                      {shift.code || shift.name}
                    </SelectItem>
                  ))}
                </React.Fragment>
              ));
            })()}
          </SelectContent>
        </Select>
      </div>

      {/* Shift Information Display */}
      {showShiftInfo && selectedShift && (
        <div className="space-y-2 w-full">
          <Label className="text-xs font-medium text-muted-foreground">
            Shift Schedule Information
          </Label>
          <div className="border rounded-lg overflow-hidden w-full">
            <Table className="w-full">
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead className="h-8 text-xs w-1/3">Days</TableHead>
                  <TableHead className="h-8 text-xs w-1/6">Start Time</TableHead>
                  <TableHead className="h-8 text-xs w-1/6">End Time</TableHead>
                  <TableHead className="h-8 text-xs w-1/6">Break</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(() => {
                  if (!selectedShift.day_time_deduction || selectedShift.day_time_deduction.length === 0) {
                    return (
                      <TableRow>
                        <TableCell colSpan={4} className="text-xs py-2 text-center text-muted-foreground">
                          No schedule information available
                        </TableCell>
                      </TableRow>
                    );
                  }

                  // Group similar schedule entries
                  const groupedSchedules = selectedShift.day_time_deduction.reduce((acc: Array<{
                    days: number[];
                    start_time: string;
                    end_time: string;
                    break_hrs: number;
                  }>, dayGroup: DayTimeDeduction) => {
                    const existingGroup = acc.find(group => 
                      group.start_time === dayGroup.start_time && 
                      group.end_time === dayGroup.end_time && 
                      group.break_hrs === (dayGroup.break_hrs || 0)
                    );

                    if (existingGroup) {
                      // Add days to existing group
                      const days = Array.isArray(dayGroup.day) ? dayGroup.day : [dayGroup.day];
                      existingGroup.days.push(...days);
                    } else {
                      // Create new group
                      const days = Array.isArray(dayGroup.day) ? dayGroup.day : [dayGroup.day];
                      acc.push({
                        days,
                        start_time: dayGroup.start_time,
                        end_time: dayGroup.end_time,
                        break_hrs: dayGroup.break_hrs || 0
                      });
                    }
                    return acc;
                  }, []);

                  return groupedSchedules.map((group, index) => {
                    // Sort days numerically and convert to readable names
                    const sortedDays = group.days.sort((a: number, b: number) => a - b);
                    const dayNames = sortedDays.map((d: number) => getDayName(d as DayNumber)).join(', ');
                    
                    return (
                      <TableRow key={index} className="h-8">
                        <TableCell className="text-xs py-1 font-medium">{dayNames}</TableCell>
                        <TableCell className="text-xs py-1">{formatShiftTime(group.start_time)}</TableCell>
                        <TableCell className="text-xs py-1">{formatShiftTime(group.end_time)}</TableCell>
                        <TableCell className="text-xs py-1">{group.break_hrs}h</TableCell>
                      </TableRow>
                    );
                  });
                })()}
              </TableBody>
            </Table>
          </div>
        </div>
      )}
    </div>
  );
}
